import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';

import { BottomDrawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { Chip } from 'uiToolkitV1/Chip';
import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

import { oneOneOneReactQueryKeys } from '../../constants/oneOnOneReactQueryKeys';
import {
  resetAgendaLibrary,
  setAgendaLibraryIsOpen,
  setAgendaLibraryType,
  useOneOnOneAgendaLibraryIsOpen,
  useOneOnOneAgendaLibraryType,
} from '../../slice/OneOnOneInstancePageSlice';
import { AgendaLibraryBody } from './components/AgendaLibraryBody';
import { useOneOnOneAgendaLibraryForSettingsQuery, useOneOnOneAgendaLibraryQuery } from './hooks/dataHooks';
import { AGENDA_LIBRARY_TYPE } from './interface';

type AGENDA_LIBRARY_OPTIONS_TYPE<T extends typeof AGENDA_LIBRARY_TYPE> = Record<T[number], string>;

const AGENDA_LIBRARY_TYPE_MAP: AGENDA_LIBRARY_OPTIONS_TYPE<typeof AGENDA_LIBRARY_TYPE> = {
  THEME: 'Themes',
  LIFECYCLE_MOMENT: 'Lifecycle Moments',
};

interface Props {
  isSettingsPage?: boolean;
  onAgendaClick?: ({ id, agendaText }: { id: number; agendaText: string }) => void;
}

const AgendaLibrary = ({ isSettingsPage = false, onAgendaClick }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const isOpen = useOneOnOneAgendaLibraryIsOpen();

  const { masterId, instanceId } = useOneOnOnePageParams();

  const selectedType = useOneOnOneAgendaLibraryType();

  const { data: instancePageData, isLoading: isLoadingInstancePage } = useOneOnOneAgendaLibraryQuery({
    masterId,
    instanceId,
    type: selectedType,
    enabled: !isSettingsPage,
  });

  const { data: settingsPageData, isLoading: isLoadingSettingsPage } = useOneOnOneAgendaLibraryForSettingsQuery({
    type: selectedType,
    enabled: isSettingsPage,
  });

  const data = isSettingsPage ? settingsPageData : instancePageData;
  const isLoading = isSettingsPage ? isLoadingSettingsPage : isLoadingInstancePage;

  const onClose = () => {
    queryClient.invalidateQueries([oneOneOneReactQueryKeys.agendaRecommendationsApi]);
    dispatch(
      setAgendaLibraryIsOpen({
        isOpen: false,
      })
    );
    dispatch(resetAgendaLibrary());
  };

  const agendaLibraryTypeOptions = Object.keys(AGENDA_LIBRARY_TYPE_MAP);

  const onTypeClick = (type: keyof typeof AGENDA_LIBRARY_TYPE_MAP) => {
    dispatch(
      setAgendaLibraryType({
        type,
      })
    );
  };

  return (
    <BottomDrawer isOpen={isOpen} onClose={onClose} placement="bottom">
      <DrawerContent width="80%">
        <DrawerHeader onClose={onClose}>
          <Box p="16px 18px">
            <TextHeader3 bold>Suggested Agenda Points</TextHeader3>
            <Flex mt="22px">
              <TextBodyMedium bold color="text.g3" mr="8px">
                Showing agenda points related to:
              </TextBodyMedium>
              {agendaLibraryTypeOptions.map((option, index) => (
                // @ts-ignore
                <Chip
                  key={`agenda-${index}`}
                  mr="8px"
                  selected={selectedType === option}
                  onClick={() => onTypeClick(option as keyof typeof AGENDA_LIBRARY_TYPE_MAP)}
                  variant="outlined"
                >
                  {AGENDA_LIBRARY_TYPE_MAP[option]}
                </Chip>
              ))}
            </Flex>
          </Box>
        </DrawerHeader>
        <StyledDrawerBody>
          <AgendaLibraryBody
            data={data}
            isLoading={isLoading}
            isSettingsPage={isSettingsPage}
            onAgendaClick={onAgendaClick}
          />
        </StyledDrawerBody>
        <DrawerFooter>
          <Button variant="tertiary" mr="auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="filled" colorScheme="blue" onClick={onClose} disabled={false}>
            Done
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </BottomDrawer>
  );
};

export { AgendaLibrary };

const StyledDrawerBody = styled(DrawerBody)`
  padding: 4px;
  padding-bottom: 0px;
`;
